import { useCallback, useEffect, useState } from 'react';
import type { KnowledgeBaseReadinessState } from '@/hooks/house/documents/knowledgeBaseReadiness';

interface AskReadinessEpisode {
	houseId: number | null;
	state: KnowledgeBaseReadinessState | null;
	attentionDocumentIds: number[];
}

interface DismissedAskReadinessEpisode {
	houseId: number | null;
	state: KnowledgeBaseReadinessState;
	documentIds: Set<number>;
}

/** Keeps a dismissal while an advisory's problem is unchanged or receding, but not when it worsens. */
export const useAskReadinessEpisodeDismissal = ({ houseId, state, attentionDocumentIds }: AskReadinessEpisode) => {
	const [dismissedEpisode, setDismissedEpisode] = useState<DismissedAskReadinessEpisode | null>(null);
	const dismissed =
		state !== null &&
		dismissedEpisode?.houseId === houseId &&
		dismissedEpisode.state === state &&
		attentionDocumentIds.every((id) => dismissedEpisode.documentIds.has(id));

	const dismiss = useCallback(() => {
		if (state) {
			setDismissedEpisode({ houseId, state, documentIds: new Set(attentionDocumentIds) });
		}
	}, [attentionDocumentIds, houseId, state]);

	useEffect(() => {
		// `dismissed` is computed synchronously so a changed episode resurfaces immediately. Clearing
		// the stale episode here records that transition, preventing A → B → A from reviving A's dismissal.
		if (dismissedEpisode && !dismissed) setDismissedEpisode(null);
	}, [dismissed, dismissedEpisode]);

	return { dismissed, dismiss } as const;
};
